/**
 * ModuleList — ordered module list with locked/unlocked/completed state.
 *
 * EPIC-08-04 AC-5
 */

import { useTranslation } from 'react-i18next';
import type { ModuleType } from '../hooks/useEnrollments';
import styles from './ModuleList.module.css';

export interface CourseModule {
  id: string;
  title: string;
  module_type: ModuleType;
  order: number;
  status: 'locked' | 'unlocked' | 'completed' | 'failed';
  score: number | null;
}

const TYPE_ICONS: Record<ModuleType, string> = {
  roleplay: '🎭',
  quiz: '📝',
  reading: '📖',
  video: '🎬',
};

interface ModuleListProps {
  modules: CourseModule[];
  onStart: (moduleId: string, moduleType: ModuleType) => void;
}

export function ModuleList({ modules, onStart }: ModuleListProps): JSX.Element {
  const { t } = useTranslation('training');

  return (
    <ol className={styles.list}>
      {modules.map((mod) => {
        const isLocked = mod.status === 'locked';
        const isCompleted = mod.status === 'completed';
        const canStart = !isLocked;

        return (
          <li
            key={mod.id}
            className={`${styles.item} ${isLocked ? styles.locked : ''} ${isCompleted ? styles.completed : ''}`}
          >
            <span className={styles.icon} aria-hidden="true">
              {isCompleted ? '✅' : isLocked ? '🔒' : TYPE_ICONS[mod.module_type]}
            </span>

            <div className={styles.info}>
              <span className={styles.title}>{mod.title}</span>
              <span className={styles.typeBadge}>
                {t(`learn.moduleType.${mod.module_type}`)}
              </span>
            </div>

            {isCompleted && mod.score !== null && (
              <span className={styles.score}>{mod.score}%</span>
            )}

            <button
              className={styles.startBtn}
              onClick={() => onStart(mod.id, mod.module_type)}
              disabled={!canStart}
              aria-label={`${t(isCompleted ? 'learn.retryModule' : 'learn.startModule')} ${mod.title}`}
            >
              {isCompleted
                ? t('learn.retryModule')
                : isLocked
                ? t('learn.lockedModule')
                : t('learn.startModule')}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
